import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { IPatientSpaceAppointment } from '../../_interfaces/IPatientSpace';
import { PatientSpaceService } from '../../_services/patient-space/patient-space.service';

@Component({
  selector: 'app-patient-appointments',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './patient-appointments.component.html',
  styleUrl: './patient-appointments.component.scss',
})
export class PatientAppointmentsComponent implements OnInit {
  loading = true;
  savingId: number | null = null;
  state: 'upcoming' | 'past' | 'pending' = 'upcoming';
  rows: IPatientSpaceAppointment[] = [];
  focusedAppointmentId: number | null = null;
  focusedBookingReference = '';
  intent: 'accept' | 'decline' | 'rebook' | null = null;

  constructor(
    private readonly patientSpaceService: PatientSpaceService,
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const queryState = `${params.get('state') || ''}`.trim();
      if (
        queryState === 'upcoming' ||
        queryState === 'past' ||
        queryState === 'pending'
      ) {
        this.state = queryState;
      }

      const appointmentId = Number(params.get('appointmentId') || 0);
      this.focusedAppointmentId = appointmentId > 0 ? appointmentId : null;
      this.focusedBookingReference = `${params.get('bookingReference') || ''}`.trim();

      const intent = `${params.get('counterProposalIntent') || ''}`
        .trim()
        .toLowerCase();
      this.intent =
        intent === 'accept' || intent === 'decline' || intent === 'rebook'
          ? intent
          : null;

      this.load();
    });
  }

  load() {
    this.loading = true;
    this.patientSpaceService.appointments({ state: this.state }).subscribe({
      next: (response: IPatientSpaceAppointment[]) => {
        this.rows = Array.isArray(response) ? response : [];
        this.loading = false;
        this.scrollToFocusedRow();
      },
      error: () => {
        this.rows = [];
        this.loading = false;
      },
    });
  }

  acceptProposal(row: any) {
    const id = Number(row?.id || 0);
    if (!id || this.savingId) return;
    this.savingId = id;
    this.patientSpaceService.acceptCounterProposal(id).subscribe({
      next: () => {
        this.savingId = null;
        this.load();
      },
      error: () => {
        this.savingId = null;
      },
    });
  }

  declineProposal(row: any) {
    const id = Number(row?.id || 0);
    if (!id || this.savingId) return;
    this.savingId = id;
    this.patientSpaceService.declineCounterProposal(id).subscribe({
      next: () => {
        this.savingId = null;
        this.load();
      },
      error: () => {
        this.savingId = null;
      },
    });
  }

  selectAnother(row: IPatientSpaceAppointment) {
    const customerIdentifier = `${row?.customer?.identifier || ''}`.trim();
    const siteIdentifier = `${row?.site?.identifier || ''}`.trim();
    if (!customerIdentifier || !siteIdentifier) return;

    this.router.navigate(
      ['/book', customerIdentifier, siteIdentifier],
      {
        queryParams: {
          fromAppointmentId: row.id,
          bookingReference: row.booking_reference || undefined,
        },
      },
    );
  }

  cancelPendingRequest(row: IPatientSpaceAppointment) {
    const id = Number(row?.id || 0);
    if (!id || this.savingId || !this.canCancelPending(row)) return;

    const confirmed = window.confirm(
      this.translate.instant('PATIENT_SPACE.APPOINTMENTS.CANCEL_PENDING_CONFIRM'),
    );
    if (!confirmed) return;

    this.savingId = id;
    this.patientSpaceService.cancelPendingRequest(id).subscribe({
      next: () => {
        this.savingId = null;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.SUCCESS'),
          detail: this.translate.instant('PATIENT_SPACE.APPOINTMENTS.CANCEL_PENDING_SUCCESS'),
        });
        this.load();
      },
      error: () => {
        this.savingId = null;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.translate.instant('PATIENT_SPACE.APPOINTMENTS.CANCEL_PENDING_ERROR'),
        });
      },
    });
  }

  isFocused(row: IPatientSpaceAppointment): boolean {
    if (this.focusedAppointmentId && Number(row?.id || 0) === this.focusedAppointmentId) {
      return true;
    }
    return (
      !!this.focusedBookingReference &&
      `${row?.booking_reference || ''}`.trim() === this.focusedBookingReference
    );
  }

  proposalHint(row: IPatientSpaceAppointment): string | null {
    if (!this.isFocused(row) || !this.intent) return null;

    const keyByIntent: Record<'accept' | 'decline' | 'rebook', string> = {
      accept: 'PATIENT_SPACE.APPOINTMENTS.INTENTS.ACCEPT',
      decline: 'PATIENT_SPACE.APPOINTMENTS.INTENTS.DECLINE',
      rebook: 'PATIENT_SPACE.APPOINTMENTS.INTENTS.REBOOK',
    };
    return this.translate.instant(keyByIntent[this.intent]);
  }

  private scrollToFocusedRow(): void {
    const focusedRow = this.rows.find((row) => this.isFocused(row));
    if (!focusedRow) return;

    setTimeout(() => {
      const element = document.getElementById(
        `patient-appointment-${focusedRow.id}`,
      );
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  appointmentStateLabel(row: IPatientSpaceAppointment): string {
    const value = this.resolveStateKey(row);
    const key = `PATIENT_SPACE.APPOINTMENTS.STATUS.${value}`;
    const translated = this.translate.instant(key);
    return translated !== key
      ? translated
      : this.translate.instant('PATIENT_SPACE.APPOINTMENTS.STATUS.UNKNOWN');
  }

  appointmentStateClass(row: IPatientSpaceAppointment): string {
    const value = this.resolveStateKey(row);
    if (value === 'APPROVED' || value === 'AUTO_ACCEPTED') {
      return 'is-approved';
    }
    if (value === 'COUNTER_PROPOSAL_PENDING') {
      return 'is-proposal';
    }
    if (value === 'PENDING_APPROVAL') {
      return 'is-pending';
    }
    if (value === 'CANCELLED') {
      return 'is-cancelled';
    }
    if (value === 'REJECTED') {
      return 'is-rejected';
    }
    return '';
  }

  canCancelPending(row: IPatientSpaceAppointment): boolean {
    return (
      `${row?.source || ''}`.trim().toUpperCase() === 'PATIENT_PORTAL' &&
      `${row?.approval_state || ''}`.trim().toUpperCase() === 'PENDING_APPROVAL' &&
      !this.isCancelled(row) &&
      !this.hasEnded(row)
    );
  }

  private resolveStateKey(row: IPatientSpaceAppointment): string {
    if (`${row?.counter_proposal?.status || ''}`.trim().toUpperCase() === 'PENDING') {
      return 'COUNTER_PROPOSAL_PENDING';
    }
    if (this.isCancelled(row)) {
      return 'CANCELLED';
    }
    return `${row?.approval_state || 'UNKNOWN'}`.trim().toUpperCase() || 'UNKNOWN';
  }

  private isCancelled(row: IPatientSpaceAppointment): boolean {
    const normalized = `${row?.status?.name || ''}`
      .trim()
      .toLowerCase();
    return normalized.includes('cancel');
  }

  private hasEnded(row: IPatientSpaceAppointment): boolean {
    const endDate = row?.end ? new Date(row.end) : row?.start ? new Date(row.start) : null;
    return !!endDate && !Number.isNaN(endDate.getTime()) && endDate.getTime() < Date.now();
  }
}
